import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import {
  CaciStyles, PageHeader, Seal,
  useReveal, langPath,
} from "../components/CaciUI";

/* ═══════════════════════════════════════════════════════════════════════════
   NOTFOUND
   ───────────────────────────────────────────────────────────────────────────
   The old version printed the same 404 string twice — once as the header
   subtitle and again as a lone centred <h1> below it — and offered nothing
   but a breadcrumb back to the homepage. A dead end is exactly where a
   visitor needs the most help, so this one names the address that failed and
   offers the four places they were most likely trying to reach.

   Also: it now tells crawlers not to index the page. Without a noindex, every
   mistyped or expired URL becomes an indexable "page not found" entry.
   ═══════════════════════════════════════════════════════════════════════════ */

function NotFound() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const lang = i18n.language;
  const isRtl = lang === "ar";
  const pageRef = useReveal([]);

  const destinations = [
    { to: langPath(lang, "/"), icon: "bi-house", label: t("menu.home") },
    { to: langPath(lang, "/toutacticle"), icon: "bi-newspaper", label: t("menu.Toutacticle", "Actualités") },
    { to: langPath(lang, "/toutpage"), icon: "bi-file-earmark-text", label: t("menu.Toutpage", "Pages") },
    { to: langPath(lang, "/search"), icon: "bi-search", label: t("menu.Search", "Recherche") },
  ];

  const title = t("home.NotFound", "Page introuvable");

  return (
    <div className={`caci-page${isRtl ? " is-rtl" : ""}`} ref={pageRef} dir={isRtl ? "rtl" : "ltr"}>
      <CaciStyles />
      <style>{PAGE_CSS}</style>

      <Helmet>
        <title>{title} — {t("menu.caci")}</title>
        <meta name="description" content={t("home.404_NotFound", "")} />
        {/* Every mistyped URL would otherwise become an indexable 404 page. */}
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <PageHeader
        title={title}
        crumbs={[{ label: t("menu.home"), to: langPath(lang, "/") }, { label: "404" }]}
      />

      <section className="caci-section">
        <div className="caci-shell">
          <div className="nf" data-rv="rise">
            <span className="nf-code" aria-hidden="true">
              <Seal size={54} spin />
              404
            </span>

            <h2 className="nf-heading">{t("home.404_NotFound", "Cette adresse n'existe pas.")}</h2>

            {/* Naming the address that failed is what turns a dead end into
                something the visitor can act on — usually they can see the
                typo themselves. */}
            <p className="nf-path" dir="ltr">
              <i className="bi bi-link-45deg" />
              <code>{location.pathname}</code>
            </p>

            <nav className="nf-links" aria-label={t("menu.home")}>
              {destinations.map((d, i) => (
                <Link key={i} to={d.to} className="nf-link" data-rv="rise" style={{ "--i": i + 1 }}>
                  <span className="nf-link-icon"><i className={`bi ${d.icon}`} /></span>
                  <span className="nf-link-label">{d.label}</span>
                  <i className={`bi bi-arrow-${isRtl ? "left" : "right"} nf-link-go`} />
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </section>
    </div>
  );
}

const PAGE_CSS = `
.caci-page .nf {
  max-width: 620px; margin-inline: auto; text-align: center;
  padding-block: clamp(8px, 3vw, 32px);
}
.caci-page .nf-code {
  display: inline-flex; align-items: center; gap: 16px;
  font-family: var(--display); font-size: clamp(3rem, 12vw, 5rem);
  font-weight: 800; letter-spacing: -.05em; line-height: 1;
  color: var(--rule-2);
}
.caci-page .nf-code .seal { color: rgba(176,138,46,.34); }
.caci-page .nf-heading {
  margin: 22px 0 0; font-family: var(--display);
  font-size: clamp(1.3rem, 4.4vw, 1.9rem); font-weight: 700;
  letter-spacing: -.026em; line-height: 1.2; color: var(--ink);
  text-wrap: balance;
}
.caci-page .nf-path {
  display: inline-flex; align-items: center; gap: 8px;
  margin: 16px 0 0; padding: 8px 16px; max-width: 100%;
  border-radius: 999px; background: var(--mint); color: var(--muted);
}
.caci-page .nf-path i { flex: 0 0 auto; font-size: 14px; }
.caci-page .nf-path code {
  font-family: var(--mono); font-size: 12.5px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.caci-page .nf-links {
  display: grid; gap: 10px; margin-block-start: clamp(28px, 4.5vw, 42px);
  text-align: start;
}
@media (min-width: 560px) { .caci-page .nf-links { grid-template-columns: 1fr 1fr; } }
.caci-page .nf-link {
  display: flex; align-items: center; gap: 14px;
  min-height: 66px; padding: 12px 18px; border-radius: 16px;
  border: 1px solid var(--rule); background: var(--surface);
  color: var(--txt); text-decoration: none;
  transition: transform var(--t1) var(--e), border-color var(--t1) var(--e),
              box-shadow var(--t1) var(--e);
}
.caci-page .nf-link:hover {
  transform: translateY(-3px); border-color: rgba(176,138,46,.45);
  box-shadow: 0 22px 40px -26px rgba(15,74,48,.5);
}
.caci-page .nf-link:active { transform: translateY(0) scale(.985); }
.caci-page .nf-link-icon {
  flex: 0 0 auto; width: 40px; height: 40px; border-radius: 12px;
  display: grid; place-items: center; font-size: 17px;
  background: var(--mint); color: var(--pine);
  transition: background var(--t1) var(--e), color var(--t1) var(--e);
}
.caci-page .nf-link:hover .nf-link-icon { background: var(--pine); color: #fff; }
.caci-page .nf-link-label {
  flex: 1; min-width: 0; font-size: 15px; font-weight: 600;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.caci-page .nf-link-go { flex: 0 0 auto; color: var(--muted); transition: transform var(--t1) var(--e); }
.caci-page .nf-link:hover .nf-link-go { transform: translateX(4px); color: var(--pine); }
.caci-page.is-rtl .nf-link:hover .nf-link-go { transform: translateX(-4px); }
`;

export default NotFound;
